import { Link } from 'react-router';
import { cn } from '@rocco/utils/cn';
import type { RevampHeaderCategory } from '../type';

interface RevampCategoryPillsProps {
    categories: RevampHeaderCategory[];
    /** Extra list classes — alignment and measure differ per title section. */
    className?: string;
    /** White hairline and white type, for a pill sitting on a photograph. */
    inverse?: boolean;
}

/**
 * Outlined pills, shared by the article revamp's title sections.
 *
 * Carries both categories and editorial themes. They are different things —
 * one is navigation, the other a subject — but the design draws them
 * identically, and keeping one component means they stay identical if it
 * changes.
 */
export const RevampCategoryPills = ({
    categories,
    className,
    inverse = false,
}: RevampCategoryPillsProps) => {
    if (categories.length === 0) return null;

    return (
        <ul
            className={cn(
                'm-0 flex max-w-full list-none flex-wrap items-center gap-[0.375rem] p-0',
                className,
            )}
        >
            {categories.map((category) => (
                // Keyed on the link, not the ID: categories and themes are
                // separate tables whose IDs collide, and both are in this list.
                // Their paths cannot collide — /articles/{segment} is unique
                // across the two by database constraint.
                <li key={category.link}>
                    <Link
                        data-head-outline=""
                        to={category.link}
                        className={cn(
                            'flex items-center justify-center rounded-full border border-solid px-[0.875rem] font-sans text-[0.75rem] leading-[1.875rem] uppercase no-underline transition-colors',
                            inverse
                                ? 'border-white/70 text-white hover:bg-white hover:text-[#181818]'
                                : 'border-black text-[#1f1f21] hover:bg-black hover:text-white',
                        )}
                    >
                        {category.title}
                    </Link>
                </li>
            ))}
        </ul>
    );
};
